'use client';

import { useRef, useState } from 'react';
import { tw } from '@tw';
import { clampText } from '@/lib/utils/clamp-text';

/**
 * Text the owner edits by typing into it: no button and no mode, the words on
 * the page are the field. Enter or leaving saves, Escape puts the saved text
 * back. Uncontrolled, so React never rewrites a node mid-typing (the caret jumps).
 */
export function EditableText({
  value,
  limit,
  label,
  placeholder,
  required,
  className,
  onSave,
}: {
  value: string;
  limit: number;
  label: string;
  /** Shown while empty, in the same ink: what a visitor reads when there is nothing. */
  placeholder?: string;
  /** Refuses an empty save with this message and restores the text. */
  required?: string;
  className?: string;
  /** Resolves to an error to show, or null once saved. */
  onSave: (next: string) => Promise<string | null>;
}) {
  const [initial] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const saved = useRef(value);

  const settle = async (el: HTMLElement) => {
    const next = (el.textContent ?? '').trim();
    if (next === saved.current) return;
    if (!next && required) {
      el.textContent = saved.current;
      return setError(required);
    }
    const failed = await onSave(next);
    if (failed) return setError(failed);
    saved.current = next;
  };

  return (
    <>
      <span
        contentEditable="plaintext-only"
        suppressContentEditableWarning
        role="textbox"
        aria-label={label}
        spellCheck={false}
        data-placeholder={placeholder}
        onInput={(e) => {
          setError(null);
          clampText(e.currentTarget, limit);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') e.currentTarget.textContent = saved.current;
          if (e.key !== 'Enter' && e.key !== 'Escape') return;
          e.preventDefault();
          e.currentTarget.blur();
        }}
        onBlur={(e) => void settle(e.currentTarget)}
        className={tw(
          'cursor-text outline-none empty:before:content-[attr(data-placeholder)]',
          className
        )}
      >
        {initial}
      </span>
      {error && (
        <span role="alert" className="sf-note block text-error">
          {error}
        </span>
      )}
    </>
  );
}
